import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { UMIcon } from './UMIcon';
import { UMCheckbox } from './UMSidePanel';
import { UMR } from './referenceTokens';

/**
 * Fluid v5 form primitives as rendered in the production UrbanMap panels
 * (Filtres, Export, Import, fiche bâtiment). Measured values: docs/urbanmap/reference/panels.md.
 * The Studio ships Fluid v6 whose React components look different: use these in UrbanMap prototypes.
 */

const INPUT_BASE: CSSProperties = {
  width: '100%',
  height: 32,
  boxSizing: 'border-box',
  border: `1px solid ${UMR.borderInput}`,
  borderRadius: 0,
  padding: '1px 16px',
  fontSize: 11,
  fontFamily: 'system-ui',
  color: UMR.black,
  background: UMR.white,
  outline: 'none',
};

const FORM_CSS =
  `.um-input::placeholder{color:${UMR.black}}.um-input:hover{background:${UMR.hover}}` +
  `.um-input:focus{border:2px solid ${UMR.blue}!important}` +
  `.um-hover-grey:hover{background:${UMR.hover}!important}` +
  `.um-hover-grey100:hover{background:${UMR.grey100}!important}` +
  `.um-tool:not(:disabled):hover{background:${UMR.blueHover}}` +
  `.um-tool[aria-pressed="true"]{background:${UMR.bluePressed}!important;border-right:2px solid ${UMR.blue}!important;border-bottom:2px solid ${UMR.blue}!important}`;

/** Injects the few pseudo-class rules inline styles cannot express. Render once per panel. */
export function UMFormStyles() {
  return <style>{FORM_CSS}</style>;
}

/** `text-base font-semibold text-gray-1000 border-b border-gray-200` (rail panels, 25px). */
export function UMSectionTitle({ children }: { children: ReactNode }) {
  return (
    <div style={{ fontSize: 16, fontWeight: 600, lineHeight: '24px', color: UMR.textStrong, borderBottom: `1px solid ${UMR.borderSubtle}` }}>
      {children}
    </div>
  );
}

/** `fluid-form-group`: 14px title, 4px gap, content. */
export function UMFormGroup({ label, children }: { label?: ReactNode; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 14, lineHeight: '21px' }}>
      {label && <label style={{ alignSelf: 'flex-start' }}>{label}</label>}
      <div>{children}</div>
    </div>
  );
}

export interface UMInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'text' | 'number';
  /** Material Icons ligature shown at the right (e.g. "search"). */
  icon?: string;
  ariaLabel?: string;
  min?: number;
}

/** `fluid-input`: 32px, 1px grey-400, square, system-ui 11px (production quirk). */
export function UMInput({ value, onChange, placeholder, type = 'text', icon, ariaLabel, min }: UMInputProps) {
  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <input
        className="um-input"
        type={type}
        min={min}
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...INPUT_BASE, padding: type === 'number' ? '1px 0 1px 16px' : INPUT_BASE.padding }}
      />
      {icon && (
        <span style={{ position: 'absolute', right: 5, top: 6, pointerEvents: 'none' }}>
          <UMIcon name={icon} family="icons" size={20} color={UMR.black} />
        </span>
      )}
    </div>
  );
}

export interface UMRange {
  min: string;
  max: string;
}

/** Min / Max pair (`flex gap-4`, two 130px number inputs). */
export function UMMinMax({ label, value, onChange }: { label: string; value: UMRange; onChange: (value: UMRange) => void }) {
  return (
    <UMFormGroup label={label}>
      <div style={{ display: 'flex', gap: 16 }}>
        <UMInput type="number" min={0} placeholder="Min" ariaLabel={`${label} min`} value={value.min} onChange={(min) => onChange({ ...value, min })} />
        <UMInput type="number" placeholder="Max" ariaLabel={`${label} max`} value={value.max} onChange={(max) => onChange({ ...value, max })} />
      </div>
    </UMFormGroup>
  );
}

/** `fluid-select`: 32px, radius 5px (the only rounded input), `expand_more` icon. */
export function UMSelect({ value, options, onChange, ariaLabel }: { value: string; options: { value: string; label: string }[]; onChange: (value: string) => void; ariaLabel?: string }) {
  return (
    <div style={{ position: 'relative' }}>
      <select
        className="um-input"
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...INPUT_BASE, borderRadius: 5, padding: '0 44px 0 16px', appearance: 'none', cursor: 'pointer' }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <span style={{ position: 'absolute', right: 3, top: 5, width: 24, height: 24, pointerEvents: 'none' }}>
        <UMIcon name="expand_more" family="icons" size={24} color={UMR.black} />
      </span>
    </div>
  );
}

/** `fluid-radio-group`: horizontal, gap 16, 16px circle (2px grey-600 / blue-600 + 10px dot). */
export function UMRadioGroup({ name, options, value, onChange }: { name: string; options: string[]; value: string; onChange: (value: string) => void }) {
  return (
    <div role="radiogroup" style={{ display: 'flex', gap: 16 }}>
      {options.map((o) => {
        const checked = o === value;
        return (
          <label key={o} style={{ display: 'flex', alignItems: 'center', gap: 8, lineHeight: '24px', cursor: 'pointer' }}>
            <input type="radio" name={name} checked={checked} onChange={() => onChange(o)} style={{ position: 'absolute', opacity: 0, width: 0, height: 0, margin: 0 }} />
            <span
              aria-hidden="true"
              style={{
                width: 16, height: 16, boxSizing: 'border-box', borderRadius: '50%', flexShrink: 0,
                border: `2px solid ${checked ? UMR.blue : UMR.checkboxBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {checked && <span style={{ width: 8, height: 8, borderRadius: '50%', background: UMR.blue }} />}
            </span>
            {o}
          </label>
        );
      })}
    </div>
  );
}

function useClickOutside(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);
  return ref;
}

/**
 * Multiselect (`custom-popover` + `p-[3px] min-h-8 border-gray-400`): selected values as grey
 * tags, `expand_more` floated right, checkbox list popover (`max-w-64`, items `p-2 hover:bg-gray-100`).
 */
export function UMMultiSelect({ options, value, onChange, ariaLabel }: { options: string[]; value: string[]; onChange: (value: string[]) => void; ariaLabel?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useClickOutside(open, () => setOpen(false));
  const toggle = (o: string, on: boolean) => onChange(on ? [...value, o] : value.filter((v) => v !== o));
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        aria-expanded={open}
        className="um-hover-grey"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setOpen((v) => !v)}
        style={{ padding: 3, minHeight: 32, boxSizing: 'border-box', border: `1px solid ${UMR.borderInput}`, background: UMR.white, cursor: 'pointer' }}
      >
        <span style={{ float: 'right', pointerEvents: 'none' }}>
          <UMIcon name="expand_more" family="icons" size={24} color={UMR.black} />
        </span>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
          {value.map((v) => (
            <span key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0 4px', background: UMR.tagBg, fontSize: 12, lineHeight: '24px' }}>
              {v}
              <span
                role="button"
                aria-label={`Retirer ${v}`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(v, false);
                }}
              >
                <UMIcon name="close" family="icons" size={14} color={UMR.textLabel} />
              </span>
            </span>
          ))}
        </div>
      </div>
      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          style={{
            position: 'absolute', top: '100%', left: 0, zIndex: 50, maxWidth: 256, maxHeight: 300, overflowY: 'auto',
            background: UMR.white, border: `1px solid ${UMR.borderInput}`,
          }}
        >
          {options.map((o) => (
            <div key={o} className="um-hover-grey100" style={{ padding: 8 }}>
              <UMCheckbox checked={value.includes(o)} onChange={(on) => toggle(o, on)} label={o} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Horizontal checkbox list (`flex gap-4`), e.g. "Type de propriétaire", "Chaud / Froid". */
export function UMCheckboxRow({ options, value, onChange }: { options: string[]; value: string[]; onChange: (value: string[]) => void }) {
  return (
    <div style={{ display: 'flex', gap: 16 }}>
      {options.map((o) => (
        <span key={o} style={{ flex: '0 0 auto' }}>
          <UMCheckbox checked={value.includes(o)} onChange={(on) => onChange(on ? [...value, o] : value.filter((v) => v !== o))} label={o} />
        </span>
      ))}
    </div>
  );
}

/** `fluid-segmented-control` (Distance aux réseaux: "Par type" / "Par nom"). Measured: grey-200 track, padding 4, items 12/24 padding 0 12, active white + bold. */
export function UMSegmentedControl({ options, value, onChange }: { options: string[]; value: string; onChange: (value: string) => void }) {
  return (
    <div role="tablist" style={{ display: 'inline-flex', padding: 4, background: UMR.borderSubtle }}>
      {options.map((o) => {
        const active = o === value;
        return (
          <button
            key={o}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o)}
            style={{
              border: 'none', borderRadius: 0, padding: '0 12px', fontFamily: UMR.font, fontSize: 12, lineHeight: '24px', cursor: 'pointer',
              background: active ? UMR.white : 'transparent', color: UMR.black, fontWeight: active ? 700 : 400,
            }}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

/** Dual range slider with black value bubbles (Distance (m): 0 m → 500+ m). */
export function UMRangeSlider({
  min,
  max,
  step,
  value,
  onChange,
  format,
}: {
  min: number;
  max: number;
  step: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  format: (v: number) => string;
}) {
  const pct = (v: number) => ((v - min) / (max - min)) * 100;
  const bubble = (v: number): CSSProperties => ({
    position: 'absolute', left: `calc(${pct(v)}% + ${0.5 - pct(v) / 100}rem)`, transform: 'translateX(-50%)',
    background: 'var(--nj-core-color-grey-900, #232d35)', color: UMR.white, fontSize: 12, fontWeight: 500, padding: '2px 8px', whiteSpace: 'nowrap', lineHeight: UMR.lhXs,
  });
  const thumb: CSSProperties = { position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-10px)', width: '100%', margin: 0, pointerEvents: 'none', background: 'transparent' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <style>{`.um-range{appearance:none;-webkit-appearance:none}.um-range::-webkit-slider-thumb{pointer-events:auto;-webkit-appearance:none;width:16px;height:16px;border-radius:50%;background:${UMR.blue};cursor:pointer}.um-range::-moz-range-thumb{pointer-events:auto;width:16px;height:16px;border-radius:50%;background:${UMR.blue};border:none;cursor:pointer}.um-range::-webkit-slider-runnable-track{background:transparent}`}</style>
      <div style={{ position: 'relative', height: 20 }}>
        <span style={bubble(value[0])}>{format(value[0])}</span>
        <span style={bubble(value[1])}>{format(value[1])}</span>
      </div>
      <div style={{ position: 'relative', height: 16 }}>
        <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-50%)', height: 2, background: UMR.borderSubtle }}>
          <div style={{ position: 'absolute', height: '100%', background: '#3c8dd6' /* prod bg-blue-500, Fluid v5 */, left: `${pct(value[0])}%`, width: `${pct(value[1]) - pct(value[0])}%` }} />
        </div>
        <input className="um-range" aria-label="Minimum" type="range" min={min} max={max} step={step} value={value[0]} onChange={(e) => onChange([Math.min(Number(e.target.value), value[1]), value[1]])} style={thumb} />
        <input className="um-range" aria-label="Maximum" type="range" min={min} max={max} step={step} value={value[1]} onChange={(e) => onChange([value[0], Math.max(Number(e.target.value), value[0])])} style={thumb} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, lineHeight: UMR.lhXs, color: UMR.text }}>
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </div>
    </div>
  );
}

/** Filtres accordion: semibold 14px label + Material Icons arrow_drop_down / arrow_right (24px grey-600). */
export function UMAccordionSection({ title, defaultOpen = false, children }: { title: string; defaultOpen?: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontFamily: UMR.font, fontSize: 14, lineHeight: '21px', color: UMR.black, textAlign: 'left' }}
      >
        <span style={{ fontWeight: 600 }}>{title}</span>
        <UMIcon name={open ? 'arrow_drop_down' : 'arrow_right'} family="icons" size={24} color={UMR.textSecondary} />
      </button>
      {open && <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</div>}
    </div>
  );
}

/** Fluid v5 `message message-info`: light blue box, 1px #7eaee9 border, padding 16, no icon. */
export function UMMessage({ title, children }: { title?: ReactNode; children?: ReactNode }) {
  return (
    <div role="status" style={{ display: 'flex', gap: 16, padding: 16, background: UMR.messageInfoBg, border: `1px solid ${UMR.messageInfoBorder}`, fontSize: 14, lineHeight: '21px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
        {title && <div>{title}</div>}
        {children && <div>{children}</div>}
      </div>
    </div>
  );
}

export interface UMButtonProps {
  children: ReactNode;
  /** primary = fluid-button-default (blue), secondary = fluid-button-button-secondary (grey), subtle = default+subtle (blue text). */
  variant?: 'primary' | 'secondary' | 'subtle';
  icon?: string;
  disabled?: boolean;
  onClick?: () => void;
  fullWidth?: boolean;
}

/** Fluid v5 `fluid-button fluid-button-s`: 32px, 12px text, padding 10px 20px, square, disabled = opacity .35. */
export function UMButton({ children, variant = 'primary', icon, disabled, onClick, fullWidth }: UMButtonProps) {
  const palette = {
    primary: { background: UMR.blue, color: UMR.white },
    secondary: { background: UMR.badgeNeutralBg, color: UMR.textStrong },
    subtle: { background: 'transparent', color: UMR.blue },
  }[variant];
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={variant === 'primary' ? undefined : 'um-hover-grey'}
      style={{
        ...palette, height: 32, boxSizing: 'border-box', padding: '10px 20px', border: 'none', borderRadius: 0,
        fontFamily: UMR.font, fontSize: 12, lineHeight: '12px', fontWeight: 400, display: 'inline-flex', alignItems: 'center',
        justifyContent: 'center', gap: 6, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.35 : 1,
        flex: fullWidth ? 1 : undefined, whiteSpace: 'nowrap',
      }}
    >
      {icon && <UMIcon name={icon} family="icons" size={16} color={palette.color} style={{ height: 12 }} />}
      {children}
    </button>
  );
}

/** Fluid v5 badge: `information` L (functional id, blue) or `neutral` M (selection dropdown). Pill shaped. */
export function UMBadge({ children, variant = 'neutral', icon, trailingIcon, onClick }: { children: ReactNode; variant?: 'information' | 'neutral'; icon?: string; trailingIcon?: string; onClick?: () => void }) {
  const info = variant === 'information';
  return (
    <span
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, borderRadius: 9999, cursor: onClick ? 'pointer' : 'default',
        padding: info ? '6px 12px' : '4px 8px', fontSize: info ? 14 : 12, lineHeight: info ? '20px' : '16px', fontWeight: info ? 700 : 400,
        background: info ? UMR.badgeInfoBg : UMR.badgeNeutralBg, color: info ? UMR.blueLink : UMR.textLabel,
      }}
    >
      {icon && <UMIcon name={icon} family="icons" size={16} color={UMR.blueLink} />}
      {trailingIcon ? (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {children}
          <UMIcon name={trailingIcon} family="icons" size={16} color={UMR.textLabel} />
        </span>
      ) : (
        children
      )}
    </span>
  );
}

export interface UMCheckboxTreeNode {
  id: string;
  label: string;
  /** Always exported, cannot be unchecked (production: "Caractéristiques principales"). */
  locked?: boolean;
  children?: UMCheckboxTreeNode[];
}

function leafIds(node: UMCheckboxTreeNode): string[] {
  return node.children?.length ? node.children.flatMap(leafIds) : [node.id];
}

/** Checkbox tree (Export > Données à exporter): parent indeterminate, children `pl-6`, `expand_less` toggle at -24px. */
export function UMCheckboxTree({ nodes, checked, onChange }: { nodes: UMCheckboxTreeNode[]; checked: string[]; onChange: (checked: string[]) => void }) {
  const [collapsed, setCollapsed] = useState<string[]>([]);
  const set = new Set(checked);
  const toggle = (node: UMCheckboxTreeNode, on: boolean) => {
    const ids = leafIds(node).filter((id) => !findNode(nodes, id)?.locked);
    const next = new Set(set);
    ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
    onChange([...next]);
  };
  const render = (node: UMCheckboxTreeNode, depth: number): ReactNode => {
    const leaves = leafIds(node);
    const n = leaves.filter((id) => set.has(id)).length;
    const all = n === leaves.length;
    const isParent = Boolean(node.children?.length);
    const open = !collapsed.includes(node.id);
    return (
      <div key={node.id}>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          {isParent && depth > 0 && (
            <button
              type="button"
              aria-expanded={open}
              aria-label={open ? 'Réduire la catégorie' : 'Développer la catégorie'}
              title={open ? 'Réduire la catégorie' : 'Développer la catégorie'}
              onClick={() => setCollapsed((c) => (open ? [...c, node.id] : c.filter((id) => id !== node.id)))}
              style={{ position: 'absolute', left: -24, height: 24, width: 16, border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <UMIcon name={open ? 'expand_less' : 'expand_more'} family="icons" size={18} color={UMR.black} style={{ height: 28 }} />
            </button>
          )}
          <span style={{ flex: '0 0 auto' }}>
            <UMCheckbox
              checked={all}
              indeterminate={isParent && n > 0 && !all}
              disabled={node.locked}
              onChange={(on) => toggle(node, on)}
              label={node.label}
            />
          </span>
        </div>
        {isParent && open && <div style={{ paddingLeft: 24 }}>{node.children!.map((c) => render(c, depth + 1))}</div>}
      </div>
    );
  };
  return <div>{nodes.map((n) => render(n, 0))}</div>;
}

function findNode(nodes: UMCheckboxTreeNode[], id: string): UMCheckboxTreeNode | undefined {
  for (const n of nodes) {
    if (n.id === id) return n;
    const found = n.children && findNode(n.children, id);
    if (found) return found;
  }
  return undefined;
}

/** Info icon with source tooltip (`bg-gray-700`, source + "Non vérifiée" toggle) used in the building record. */
export function UMSourceInfo({ source, warning, verified, onVerifiedChange }: { source: string; warning?: boolean; verified?: boolean; onVerifiedChange?: (v: boolean) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useClickOutside(open, () => setOpen(false));
  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button type="button" aria-label="Source de la donnée" onClick={() => setOpen((v) => !v)} style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', display: 'flex' }}>
        <UMIcon name="info" family="icons" size={12} color={warning ? UMR.warning : UMR.textLabel} />
      </button>
      {open && (
        <span
          role="tooltip"
          style={{
            position: 'absolute', bottom: 'calc(100% + 4px)', left: -8, zIndex: 60, width: 'max-content', minWidth: 170, maxWidth: 260,
            background: UMR.tooltipBg, color: UMR.white, padding: 8, display: 'flex', flexDirection: 'column', gap: 12, fontSize: 14, lineHeight: '21px', fontWeight: 400,
          }}
        >
          <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 4 }}>
            <span style={{ wordBreak: 'break-word' }}>{source}</span>
            <button type="button" aria-label="Fermer" onClick={() => setOpen(false)} style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', display: 'flex' }}>
              <UMIcon name="close" family="icons" size={16} color={UMR.white} />
            </button>
          </span>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!verified} onChange={(e) => onVerifiedChange?.(e.target.checked)} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
            <span aria-hidden="true" style={{ width: 32, height: 16, borderRadius: 8, background: verified ? UMR.blue : UMR.borderInput, position: 'relative', flexShrink: 0 }}>
              <span style={{ position: 'absolute', top: 2, left: verified ? 18 : 2, width: 12, height: 12, borderRadius: '50%', background: UMR.white }} />
            </span>
            {verified ? 'Vérifiée' : 'Non vérifiée'}
          </label>
        </span>
      )}
    </span>
  );
}
